import React, { useState, useEffect } from 'react';
import { Search, Plus, Target, Pencil, Archive, X, Tag as TagIcon, ArchiveRestore, Layers } from 'lucide-react';
import { CATEGORIES, CATEGORY_COLORS, INITIAL_COMPETENCIES, LEVELS, LEVEL_SHORT, appliesToLevel, isUniversal, sortLevels } from '../data/competencies';
import type { Competency, Level } from '../data/competencies';
type DraftState = {
  name: string;
  description: string;
  category: string;
  levels: Level[];
  tags: string;
};
const EMPTY_DRAFT: DraftState = {
  name: '',
  description: '',
  category: '',
  levels: [],
  tags: ''
};
export function CompetencyLibrary() {
  const [competencies, setCompetencies] = useState<Competency[]>(INITIAL_COMPETENCIES);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeLevel, setActiveLevel] = useState<Level | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Competency | null>(null);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const allTags = Array.from(new Set(competencies.flatMap(c => c.tags))).sort();
  const filtered = competencies.filter(c => {
    if (c.archived !== showArchived) return false;
    if (activeCategory && c.category !== activeCategory) return false;
    if (activeLevel && !appliesToLevel(c, activeLevel)) return false;
    if (activeTag && !c.tags.includes(activeTag)) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setDrawerOpen(true);
  };
  const openEdit = (c: Competency) => {
    setEditing(c);
    setDraft({
      name: c.name,
      description: c.description,
      category: c.category,
      levels: c.levels,
      tags: c.tags.join(', ')
    });
    setDrawerOpen(true);
  };
  const toggleArchive = (id: number) => {
    setCompetencies(prev => prev.map(c => c.id === id ? {
      ...c,
      archived: !c.archived
    } : c));
  };
  const toggleDraftLevel = (level: Level) => {
    setDraft(prev => ({
      ...prev,
      levels: prev.levels.includes(level) ? prev.levels.filter(l => l !== level) : sortLevels([...prev.levels, level])
    }));
  };
  const parseTags = (value: string) => value.split(',').map(t => t.trim()).filter(Boolean);
  const canSave = draft.name.trim().length > 0 && draft.category.length > 0;
  const handleSave = () => {
    if (!canSave) return;
    const tags = parseTags(draft.tags);
    if (editing) {
      setCompetencies(prev => prev.map(c => c.id === editing.id ? {
        ...c,
        name: draft.name.trim(),
        description: draft.description.trim(),
        category: draft.category,
        levels: sortLevels(draft.levels),
        tags
      } : c));
    } else {
      setCompetencies(prev => [...prev, {
        id: Math.max(0, ...prev.map(c => c.id)) + 1,
        name: draft.name.trim(),
        description: draft.description.trim(),
        category: draft.category,
        levels: sortLevels(draft.levels),
        tags,
        questions: 0,
        archived: false
      }]);
    }
    setDrawerOpen(false);
  };

  // Close the drawer on Escape for keyboard users.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);
  const activeFilterCount = (activeCategory ? 1 : 0) + (activeLevel ? 1 : 0) + (activeTag ? 1 : 0);
  return <div className="animate-in fade-in duration-500" data-id="element-796" data-mp="1glcaczqdedpt">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-797" data-mp="1bnaqwdp18cdt">
        <div data-id="element-798" data-mp="29aj65qyfwp7d">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-799" data-mp="29b2p9azttjmz">
            Competency Library
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-800" data-mp="24crhr6x07l7x">
            Browse, create, and manage the competencies used in your
            assessments.
          </p>
        </div>
        <button onClick={openCreate} className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center shrink-0" data-id="element-801" data-mp="1udsfq76quntw">
          <Plus className="h-4 w-4 mr-2" data-id="element-802" data-mp="ixvh1r93spjh" />
          New Competency
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-803" data-mp="2um8o10im9e4q">
        {/* Sidebar: Categories + Levels */}
        <aside className="w-full lg:w-56 shrink-0 space-y-6" data-id="element-804" data-mp="2zka55fwhf3dk">
          <div data-id="element-805" data-mp="2ziqr7vyat4ey">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 px-3" data-id="element-806" data-mp="34jwlhy7lwzql">
              Categories
            </h3>
            <nav className="space-y-0.5" data-id="element-807" data-mp="39bo8xzvgupci">
              <button onClick={() => setActiveCategory(null)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeCategory === null ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-808" data-mp="39cfxwruk5ott">
                All Categories
              </button>
              {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${activeCategory === cat ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-809" data-mp="3s7echrj1rtsi">
                  <span className="truncate" data-id="element-810" data-mp="1luhtq90rczl4">{cat}</span>
                  <span className="text-xs text-slate-400 ml-2" data-id="element-811" data-mp="1zsocehx4yt07">
                    {competencies.filter(c => c.category === cat && !c.archived).length}
                  </span>
                </button>)}
            </nav>
          </div>

          <div className="border-t border-slate-200 pt-4" data-id="element-812" data-mp="24rj3061chluv">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 px-3 flex items-center" data-id="element-813" data-mp="2ijyx2hc9q9jn">
              <Layers className="h-3.5 w-3.5 mr-1.5" data-id="element-814" data-mp="2dldz6okho1yd" />
              Levels
            </h3>
            <nav className="space-y-0.5" data-id="element-815" data-mp="2sgtsush5pvu8">
              <button onClick={() => setActiveLevel(null)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeLevel === null ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-816" data-mp="2sfitd4uqva8x">
                All Levels
              </button>
              {LEVELS.map(level => <button key={level} onClick={() => setActiveLevel(level)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${activeLevel === level ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-817" data-mp="2x5mi04qapi3k">
                  <span className="truncate" data-id="element-818" data-mp="2egfzahpi4qhr">{level}</span>
                  <span className="text-xs text-slate-400 ml-2" data-id="element-819" data-mp="2edtsikafqf0l">
                    {competencies.filter(c => !c.archived && appliesToLevel(c, level)).length}
                  </span>
                </button>)}
            </nav>
            <p className="text-xs text-slate-400 mt-2 px-3 leading-relaxed" data-id="element-820" data-mp="2oyuvw040523s">
              Counts include competencies that apply to all levels.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4" data-id="element-821" data-mp="2fxmyrld827ba">
            <button onClick={() => setShowArchived(!showArchived)} className={`w-full flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${showArchived ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-822" data-mp="2fw247vqwqodf">
              <Archive className="h-4 w-4 mr-2 text-slate-400" data-id="element-823" data-mp="217fss54eigrh" />
              {showArchived ? 'Viewing Archived' : 'View Archived'}
            </button>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-4" data-id="element-824" data-mp="1s67sjhibr5ur">
          {/* Search + Tags */}
          <div className="space-y-3" data-id="element-825" data-mp="1n8pvaodo6f2l">
            <div className="relative" data-id="element-826" data-mp="1ia4p88bql168">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-827" data-mp="1ib64kozsc9tr" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search competencies by name or description..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-828" data-mp="186zfvmj375x8" />
            </div>
            <div className="flex flex-wrap items-center gap-2" data-id="element-829" data-mp="ph19dauxtkvp">
              <span className="text-xs font-medium text-slate-400 flex items-center mr-1" data-id="element-830" data-mp="uejip2cva248">
                <TagIcon className="h-3.5 w-3.5 mr-1" data-id="element-831" data-mp="zdw3zio3b14p" /> Tags:
              </span>
              {allTags.map(tag => <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${activeTag === tag ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-832" data-mp="144uo6tc78jd8">
                  {tag}
                </button>)}
            </div>
            {activeLevel && <div className="flex items-center gap-2 text-sm text-slate-600 bg-violet-50 border border-violet-100 rounded-md px-3 py-2" data-id="element-833" data-mp="30vzmqebvujl7">
                <Layers className="h-4 w-4 text-violet-500 shrink-0" data-id="element-834" data-mp="34x1ob1t9v7qc" />
                <span data-id="element-835" data-mp="288mylzq2q3ps">
                  Showing competencies for{' '}
                  <span className="font-medium text-slate-900" data-id="element-836" data-mp="2d6f1fos9ba8r">
                    {activeLevel}
                  </span>
                </span>
                <button onClick={() => setActiveLevel(null)} className="ml-auto text-xs font-medium text-violet-700 hover:text-violet-900 shrink-0" data-id="element-837" data-mp="2h6p9d6zm0aq9">
                  Clear
                </button>
              </div>}
          </div>

          {/* Results */}
          {filtered.length === 0 ? <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center bg-white" data-id="element-838" data-mp="1lzojrz2fzeun">
              <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-839" data-mp="1gztws6lsf9ev">
                <Target className="h-6 w-6 text-slate-400" data-id="element-840" data-mp="1cx5ktq1s1mdo" />
              </div>
              <h3 className="text-sm font-medium text-slate-900" data-id="element-841" data-mp="1806tff9hztj6">
                No competencies found
              </h3>
              <p className="text-sm text-slate-500 mt-1 mb-4 max-w-sm mx-auto" data-id="element-842" data-mp="1312kmpqhjxtd">
                {showArchived ? 'There are no archived competencies.' : activeFilterCount > 0 ? `No competencies match the ${activeFilterCount === 1 ? 'active filter' : 'active filters'}${activeLevel ? ` for ${activeLevel}` : ''}.` : 'Try adjusting your search or create a new competency.'}
              </p>
              <div className="flex items-center justify-center gap-2" data-id="element-843" data-mp="fa6m79riy4d5">
                {activeLevel && <button onClick={() => setActiveLevel(null)} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50 inline-flex items-center" data-id="element-844" data-mp="5mukuotlgkon">
                    Clear level filter
                  </button>}
                {!showArchived && <button onClick={openCreate} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50 inline-flex items-center" data-id="element-845" data-mp="odvjtcupsg87">
                    <Plus className="h-4 w-4 mr-1.5" data-id="element-846" data-mp="en98vvxy0dvu" /> New Competency
                  </button>}
              </div>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-847" data-mp="17by0939754ue">
              {filtered.map(c => <div key={c.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col group hover:border-slate-300 transition-colors" data-id="element-848" data-mp="1c3g17rb7z5fd">
                  <div className="flex items-start justify-between mb-2" data-id="element-849" data-mp="2rtpecfq540zh">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${CATEGORY_COLORS[c.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-id="element-850" data-mp="36jo7u56r2o8m">
                      {c.category}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-851" data-mp="28wfsyt198dfl">
                      <button onClick={() => openEdit(c)} title="Edit" aria-label={`Edit ${c.name}`} className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-852" data-mp="2mwqls1m4k23e">
                        <Pencil className="h-4 w-4" data-id="element-853" data-mp="3ub20ydcq6nun" />
                      </button>
                      <button onClick={() => toggleArchive(c.id)} title={c.archived ? 'Restore' : 'Archive'} aria-label={`${c.archived ? 'Restore' : 'Archive'} ${c.name}`} className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-854" data-mp="tfcfy2yywuos">
                        {c.archived ? <ArchiveRestore className="h-4 w-4" data-id="element-855" data-mp="1cajbb5mpap3y" /> : <Archive className="h-4 w-4" data-id="element-856" data-mp="1gc4jwes7ch8c" />}
                      </button>
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900" data-id="element-857" data-mp="1q2z6jk9d1iub">
                    {c.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 flex-1" data-id="element-858" data-mp="1v4ohz10hvyix">
                    {c.description}
                  </p>

                  {/* Levels */}
                  <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-859" data-mp="3kkit0wbm1zv1">
                    {isUniversal(c) ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200" data-id="element-860" data-mp="3fkwbwbsnvzgy">
                        All levels
                      </span> : sortLevels(c.levels).map(l => <span key={l} title={l} className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100" data-id="element-861" data-mp="75vuqcuymb80">
                          {LEVEL_SHORT[l]}
                        </span>)}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100" data-id="element-862" data-mp="pw3viob6spl1">
                    <div className="flex flex-wrap gap-1.5" data-id="element-863" data-mp="l4vo1xtf3eai">
                      {c.tags.map(t => <span key={t} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600" data-id="element-864" data-mp="2fe0rwqcifvvj">
                          {t}
                        </span>)}
                    </div>
                    <span className="text-xs text-slate-400 shrink-0 ml-2" data-id="element-865" data-mp="2aky46yuezgn9">
                      {c.questions} questions
                    </span>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Create / Edit Drawer */}
      {drawerOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-866" data-mp="1mqep3kb3wv9q">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setDrawerOpen(false)} data-id="element-867" data-mp="1rog5tyl28ii1" />
          <div role="dialog" aria-modal="true" aria-label={editing ? 'Edit competency' : 'New competency'} className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-868" data-mp="3h0i8btjaf8uo">
            <div className="flex items-center justify-between px-6 h-16 border-b border-slate-200" data-id="element-869" data-mp="15cvdlhnuwjwr">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-870" data-mp="1k5p2ftp0qce3">
                {editing ? 'Edit Competency' : 'New Competency'}
              </h2>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600" data-id="element-871" data-mp="1t3ixz2eyl207">
                <X className="h-5 w-5" data-id="element-872" data-mp="23301bd6qn3t0" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-873" data-mp="1y3wx1uyl11m3">
              <div data-id="element-874" data-mp="1y1kcrb5cqf5v">
                <label htmlFor="competency-name" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-875" data-mp="8op7srf77lgw">
                  Name *
                </label>
                <input id="competency-name" type="text" value={draft.name} onChange={e => setDraft({
              ...draft,
              name: e.target.value
            })} placeholder="e.g., Strategic Vision" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-876" data-mp="3e2cnfhfphmke" />
              </div>
              <div data-id="element-877" data-mp="303whhspo403b">
                <label htmlFor="competency-description" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-878" data-mp="351ef4my8isw0">
                  Description
                </label>
                <textarea id="competency-description" rows={3} value={draft.description} onChange={e => setDraft({
              ...draft,
              description: e.target.value
            })} placeholder="Describe what this competency measures..." className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-879" data-mp="kbp6iro4k3br" />
              </div>
              <div data-id="element-880" data-mp="1q4v36qvsl2zj">
                <label htmlFor="competency-category" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-881" data-mp="1q5d7maslynij">
                  Category *
                </label>
                <select id="competency-category" value={draft.category} onChange={e => setDraft({
              ...draft,
              category: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-882" data-mp="1h58biwsv7bjo">
                  <option value="" disabled data-id="element-883" data-mp="3uvwhs5qzap9a">
                    Select a category
                  </option>
                  {CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-884" data-mp="3l2ywxsyiko7i">
                      {cat}
                    </option>)}
                </select>
              </div>

              {/* Levels multi-select */}
              <div data-id="element-885" data-mp="3c2iwytgzbz7e">
                <span className="block text-sm font-medium text-slate-700 mb-2" data-id="element-886" data-mp="3c20sj9k5yeoe">
                  Applies to levels
                </span>
                <div className="flex flex-wrap gap-2" data-id="element-887" data-mp="325nxo8cvtus2">
                  {LEVELS.map(level => {
                const selected = draft.levels.includes(level);
                return <button key={level} type="button" onClick={() => toggleDraftLevel(level)} aria-pressed={selected} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-888" data-mp="10e15i3h97tu0">
                        {level}
                      </button>;
              })}
                </div>
                <p className="text-xs text-slate-400 mt-2" data-id="element-889" data-mp="tx9fklp58t5p">
                  {draft.levels.length === 0 ? 'None selected — this competency will apply to every level.' : `Applies to ${draft.levels.length} of ${LEVELS.length} levels.`}
                </p>
              </div>

              <div data-id="element-890" data-mp="2ackap83dci8g">
                <label htmlFor="competency-tags" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-891" data-mp="2aazi3o0m79d8">
                  Tags
                </label>
                <input id="competency-tags" type="text" value={draft.tags} onChange={e => setDraft({
              ...draft,
              tags: e.target.value
            })} placeholder="Comma-separated, e.g. vision, planning" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-892" data-mp="f71hka02bfbz" />
                <p className="text-xs text-slate-400 mt-1" data-id="element-893" data-mp="5aomb7ovctf4">
                  Separate tags with commas.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-894" data-mp="hvmmy89cb9w">
              {editing ? <button onClick={() => {
            toggleArchive(editing.id);
            setDrawerOpen(false);
          }} className="text-sm font-medium text-red-600 hover:text-red-700 flex items-center" data-id="element-895" data-mp="34wvdof9hzzvy">
                  <Archive className="h-4 w-4 mr-1.5" data-id="element-896" data-mp="2kwbuwdtceg45" />
                  {editing.archived ? 'Restore' : 'Archive'}
                </button> : <span data-id="element-897" data-mp="2psk7pkk2hx85" />}
              <div className="flex gap-3" data-id="element-898" data-mp="3dk7r7hf0260e">
                <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-899" data-mp="3dl913ika4mgj">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={!canSave} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-900" data-mp="38jthzw1nwq0s">
                  {editing ? 'Save Changes' : 'Create Competency'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}